import { useState } from "react";

import InputComponent from "V2.0/components/InputComponent";
import { Button } from "V2.0/common/button";
import { MicrophoneIcon } from "@heroicons/react/16/solid";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { createDesignedVoice, designVoice } from "../../service";
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
} from "V2.0/common/table";
import { ArrowLeftIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import PromptVoiceAdd from "./PromptVoiceAdd";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import usePromptVoiceState from "./State/usePromptVoiceState";

enum Page {
  Form = "Form",
  Results = "Results",
}

export default function Prompt(props) {
  const { fetchVoices, onClose } = props;

  const [page, setPage] = useState(Page.Form);
  const [voiceResults, setVoiceResults] = useState({
    previews: [],
    text: "",
    isLoading: false,
  });

  const {
    promptVoiceState,
    updateVoiceDescription,
    updateText,
    validatePromptVoiceState,
  } = usePromptVoiceState();
  const [openPromptVoiceAdd, setOpenPromptVoiceAdd] = useState({
    isOpen: false,
    generatedVoiceId: null,
  });

  const handleGenerateVoices = async () => {
    if (!validatePromptVoiceState()) {
      return;
    }

    setVoiceResults((prevResults) => {
      return {
        ...prevResults,
        isLoading: true,
      };
    });
    const payload = {
      voiceDescription: promptVoiceState.voiceDescription,
      text: promptVoiceState.text,
    };

    message.loading("Generating Voices");

    try {
      const response = await designVoice({ payload });

      if (response.status === 200) {
        message.destroy();
        setPage(Page.Results);
        setVoiceResults({
          previews: response.data?.previews ?? [],
          text: response.data?.text ?? "",
          isLoading: false,
        });
      }
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to generate voices");
      setVoiceResults((prevResults) => {
        return {
          ...prevResults,
          isLoading: false,
        };
      });
    }
  };

  async function handleVoiceAdd(voiceName, lifecycleCbs) {
    const payload = {
      voiceName,
      generatedVoiceId: openPromptVoiceAdd.generatedVoiceId,
      description: promptVoiceState.voiceDescription,
    };

    lifecycleCbs.onInit();

    try {
      const response = await createDesignedVoice({ payload });

      if (response.status === 200) {
        lifecycleCbs.onSuccess();
        setOpenPromptVoiceAdd({
          isOpen: false,
          generatedVoiceId: null,
        });
        fetchVoices();
      }
    } catch (e) {
      lifecycleCbs.onError(e);
    }
  }

  const isGenerateDisabled = Object.values(promptVoiceState.errors).some(
    (error) => Boolean(error)
  );

  return (
    <>
      <Dialog
        open={true}
        onClose={() => {}}
        size="6xl"
      >
        <DialogTitle>Voice Design — Prompt</DialogTitle>
        <DialogBody>
          <div className="flex flex-col gap-y-6 w-full min-h-[calc(100vh-300px)]">
            {page === Page.Form && (
              <div className="h-full w-2/4 mx-auto my-auto p-8 flex flex-col gap-y-8 justify-center items-center">
                <div className="w-full">
                  <InputComponent
                    type="textArea"
                    value={promptVoiceState.voiceDescription}
                    style={{ height: "100px", fontSize: "13px" }}
                    label="Voice Description Prompt *"
                    description="Describe the voice's tone, style, emotion, accent, etc"
                    error={promptVoiceState.errors["voiceDescription"]}
                    onChange={(e) => updateVoiceDescription(e.target.value)}
                  />
                </div>
                <div className="w-full">
                  <InputComponent
                    type="textArea"
                    value={promptVoiceState.text}
                    style={{ height: "150px", fontSize: "13px" }}
                    label="Speech Text (optional)"
                    description="Enter the text you want the voice to read alound"
                    error={promptVoiceState.errors["text"]}
                    onChange={(e) => updateText(e.target.value)}
                  />
                </div>

                <Button
                  className="w-full"
                  disabled={isGenerateDisabled}
                  onClick={handleGenerateVoices}
                  loading={voiceResults.isLoading}
                >
                  Generate
                </Button>
              </div>
            )}
            {page === Page.Results && (
              <div className="flex flex-col gap-y-6 p-2 h-full">
                <div>
                  <Button
                    plain
                    onClick={() => {
                      setPage(Page.Form);
                      setVoiceResults({
                        previews: [],
                        text: "",
                        isLoading: false,
                      });
                    }}
                  >
                    <ArrowLeftIcon /> back
                  </Button>
                </div>
                {!voiceResults.isLoading &&
                  voiceResults.previews.length === 0 && (
                    <div className="flex items-center justify-center border rounded-lg h-full flex-1">
                      <MicrophoneIcon className="size-8" /> No Voices Found
                    </div>
                  )}
                {voiceResults.isLoading && (
                  <div className="flex items-center justify-center border rounded-lg h-full flex-1">
                    <AiOutlineLoading3Quarters className="animate-spin size-8" />
                  </div>
                )}
                {!voiceResults.isLoading &&
                  voiceResults.previews.length > 0 && (
                    <div className="h-full min-h-0 flex-1 overflow-auto w-3/4 mx-auto">
                      <Table>
                        <TableHead>
                          <TableHeader>Voice</TableHeader>
                          <TableHeader>Action</TableHeader>
                        </TableHead>
                        <TableBody>
                          {voiceResults.previews.map((preview, idx) => {
                            const { audioBase64, generatedVoiceId } = preview;

                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <audio
                                    controls
                                    src={`data:audio/mpeg;base64,${audioBase64}`}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    outline
                                    onClick={() =>
                                      setOpenPromptVoiceAdd({
                                        isOpen: true,
                                        generatedVoiceId,
                                      })
                                    }
                                  >
                                    <PencilSquareIcon /> Add
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            outline
            onClick={onClose}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {openPromptVoiceAdd.isOpen && (
        <PromptVoiceAdd
          onVoiceAdd={handleVoiceAdd}
          onClose={() =>
            setOpenPromptVoiceAdd({
              isOpen: false,
              generatedVoiceId: null,
            })
          }
        />
      )}
    </>
  );
}
